/*
 * Copyright (c) 2026 Paxia AI Pty Ltd. All rights reserved.
 * This software is proprietary and confidential.
 * No part of this codebase may be copied, modified, published, distributed, sublicensed, or used without prior written permission from Paxia AI Pty Ltd.
 */

import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Responsive, WidthProvider } from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import {
  listWatchlistItems,
  listStudioLayouts,
  createStudioLayout,
  updateStudioLayout,
  deleteStudioLayout,
  getUserProfile,
  getSymbolInfo, // ⬅️ add this
} from '../api/api';
import TradingChart from '../components/TradingChart';
import TradePanel from '../components/TradePanel';
import OpenPositionsWidget from '../components/dashboard-widgets/OpenPositionsWidget';
import EquityCurveWidget from '../components/dashboard-widgets/EquityCurveWidget';
import WatchlistWidget from '../components/dashboard-widgets/WatchlistWidget';
import TradeHistoryWidget from '../components/dashboard-widgets/TradeHistoryWidget';
// New widgets
import NewsWidget from '../components/dashboard-widgets/NewsWidget';
import COTWidget from '../components/dashboard-widgets/COTWidget';
import ClosedPositionsWidget from '../components/dashboard-widgets/ClosedPositionsWidget';
import JournalWidget from '../components/dashboard-widgets/JournalWidget';
import AccountsWidget from '../components/dashboard-widgets/AccountsWidget';
import AccountBalanceWidget from '../components/dashboard-widgets/AccountBalanceWidget';
import { Card, CardContent } from '../components/ui/card';
// Add: Economic Calendar widget
import EconomicCalendarWidget from '../components/fundamentals/EconomicCalendarWidget';
import CurrencyNarrativesWidget from '../components/dashboard-widgets/CurrencyNarrativesWidget';
const ResponsiveGridLayout = WidthProvider(Responsive);
import { useTrade } from '../context/TradeProvider';
import { useAccountData } from '../context/AccountDataProvider';
import { setDigitsFor } from '../utils/symbolDigitsStore';
import AccountSelection from '../components/AccountSelection';
import SymbolSelection from '../components/SymbolSelection';
import CustomSelect from '../components/ui/CustomSelect';
import { useTradeState } from '../hooks/useTradeState';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
// NEW: UI utilities for menus and modals
import { Modal } from '../components/ui/modal';
import { Select, SelectContent, SelectItem, SelectTrigger } from '../components/ui/select';
import { GripVertical, Magnet, Lock, LockOpen, Plus, LayoutGrid, ChevronDown } from 'lucide-react';

// Timeframes
const timeframes = [
  { label: '1 Minute', value: 'M1' },
  { label: '5 Minutes', value: 'M5' },
  { label: '15 Minutes', value: 'M15' },
  { label: '30 Minutes', value: 'M30' },
  { label: '1 Hour', value: 'H1' },
  { label: '4 Hours', value: 'H4' },
  { label: '1 Day', value: 'D1' },
  { label: '1 Week', value: 'W1' },
  { label: '1 Month', value: 'MN1' },
];

// Widget definitions
const widgetMeta = {
  chart: {
    label: 'Trading Chart',
    defaultW: 42,
    defaultH: 19,
    minW: 10,
    minH: 8,
  render: ({ accountId, symbol, timeframe, pipSize, tickSize, digits, stopLossPrice, takeProfitPrice, openPositions, setTradeContext, showRecentTrades, onToggleRecentTrades }) => (
      // Removed per-widget PriceFeedProvider; now provided at Studio level
      <TradingChart
        selectedAccount={accountId}
        selectedSymbol={symbol}
        selectedTimeframe={timeframe}
        timeframeOptions={timeframes}
        onTimeframeChange={(newTf) => setTradeContext({ timeframe: newTf })}
        tickSize={tickSize}
        pipSize={pipSize}
        digits={digits}
        stopLossPrice={stopLossPrice}
        takeProfitPrice={takeProfitPrice}
        openPositions={openPositions}
        showRecentTrades={showRecentTrades}
        onToggleRecentTrades={onToggleRecentTrades}
        enableStudioChartAnalysis
      />
    ),
  },
  tradePanel: {
    label: 'Trade Panel',
    defaultW: 15,
    defaultH: 19,
    minW: 6,
    minH: 8,
    render: ({
      pipSize,
      tickSize,
      contractSize,
      stopLossPrice,
      setStopLossPrice,
      takeProfitPrice,
      setTakeProfitPrice,
    }) => (
      // Removed per-widget PriceFeedProvider; now provided at Studio level
      <TradePanel
        // symbol info (lifted)
        pipSize={pipSize}
        tickSize={tickSize}
        contractSize={contractSize}
        // shared SL/TP state (lifted)
        stopLossPrice={stopLossPrice}
        setStopLossPrice={setStopLossPrice}
        takeProfitPrice={takeProfitPrice}
        setTakeProfitPrice={setTakeProfitPrice}
      />
    ),
  },
  openPositions: {
    label: 'Open Positions',
    defaultW: 12,
    defaultH: 6,
    minW: 6,
    minH: 3,
    render: () => <OpenPositionsWidget hideTitle />,
  },
  equityCurve: {
    label: 'Equity Curve',
    defaultW: 6,
    defaultH: 10,
    minW: 4,
    minH: 5,
    render: ({ accountId }) => <EquityCurveWidget accountId={accountId} hideTitle />,
  },
  tradeHistory: {
    label: 'Trade History',
    defaultW: 20,
    defaultH: 18,
    minW: 8,
    minH: 5,
    render: ({ accountId }) => (
      <TradeHistoryWidget accountId={accountId} hideTitle compact />
    ),
  },
  // New: Economic Calendar widget
  economicCalendar: {
    label: 'Economic Calendar',
    defaultW: 6,
    defaultH: 12,
    minW: 4,
    minH: 5,
    render: () => (
      <EconomicCalendarWidget
        title="Economic Calendar"
        compact={true}
        showFilters={false}
        limit={12}
        autoRefreshMs={0}
        showChart={true}
        showViewAllLink={true}
        hideTitle
      />
    ),
  },
  // New: Watchlist widget
  watchlist: {
    label: 'Watchlist',
    defaultW: 7,
    defaultH: 4,
    minW: 4,
    minH: 4,
    render: ({ accountId, widgetSettings, setWidgetSetting, isCustomizing }) => (
      <WatchlistWidget
        accountId={accountId}
        restrictToAccount
        layoutMode={widgetSettings?.layoutMode || 'auto'}
        onLayoutModeChange={(layoutMode) => setWidgetSetting({ layoutMode })}
        flatColumnWidths={widgetSettings?.flatColumnWidths}
        onFlatColumnWidthsChange={(flatColumnWidths) => setWidgetSetting({ flatColumnWidths })}
        isCustomizing={isCustomizing}
      />
    ),
  },
  watchlistFlat: {
    label: 'Flat Watchlist',
    defaultW: 8,
    defaultH: 8,
    minW: 4,
    minH: 4,
    render: ({ accountId, widgetSettings, setWidgetSetting, isCustomizing }) => (
      <WatchlistWidget
        accountId={accountId}
        restrictToAccount
        layoutMode={widgetSettings?.layoutMode || 'auto'}
        onLayoutModeChange={(layoutMode) => setWidgetSetting({ layoutMode })}
        flatColumnWidths={widgetSettings?.flatColumnWidths}
        onFlatColumnWidthsChange={(flatColumnWidths) => setWidgetSetting({ flatColumnWidths })}
        instrumentStyle="flat"
        isCustomizing={isCustomizing}
      />
    ),
  },
  // News widget
  news: {
    label: 'News',
    defaultW: 8,
    defaultH: 12,
    minW: 4,
    minH: 5,
    render: () => <NewsWidget hideTitle limit={15} autoRefreshMs={60000} />,
  },
  // COT (Commitment of Traders) widget
  cot: {
    label: 'COT Report',
    defaultW: 8,
    defaultH: 14,
    minW: 4,
    minH: 5,
    render: () => <COTWidget hideTitle limit={10} />,
  },
  // Closed Positions widget
  closedPositions: {
    label: 'Closed Positions',
    defaultW: 14,
    defaultH: 10,
    minW: 6,
    minH: 4,
    render: ({ accountId }) => <ClosedPositionsWidget accountId={accountId} hideTitle limit={50} />,
  },
  // Trade Journal widget
  journal: {
    label: 'Journal',
    defaultW: 10,
    defaultH: 12,
    minW: 5,
    minH: 5,
    render: ({ accountId }) => <JournalWidget accountId={accountId} hideTitle limit={20} />,
  },
  // Accounts Overview widget
  accounts: {
    label: 'Accounts',
    defaultW: 8,
    defaultH: 10,
    minW: 4,
    minH: 4,
    render: () => <AccountsWidget hideTitle compact={false} />,
  },
  // Account Balance widget (compact with live updates)
  accountBalance: {
    label: 'Account Balance',
    defaultW: 6,
    defaultH: 8,
    minW: 4,
    minH: 4,
    render: ({ accountId }) => <AccountBalanceWidget accountId={accountId} restrictToAccount />,
  },
  // Currency Narratives widget
  currencyNarratives: {
    label: 'Currency Narratives',
    defaultW: 10,
    defaultH: 14,
    minW: 5,
    minH: 5,
    render: () => <CurrencyNarrativesWidget hideTitle limit={20} autoRefreshMs={120000} />,
  },
};

const HIDDEN_WIDGET_TYPES = new Set([
  'accounts',
  'accountBalance',
  'journal',
  'closedPositions',
]);

const WIDGET_TYPE_ALIASES = {
  currencyNarrative: 'currencyNarratives',
  watchlist_v2: 'watchlist',
  watchlist_flat: 'watchlistFlat',
};

const WATCHLIST_LAYOUT_MODES = new Set(['auto', 'vertical', 'horizontal']);
const WATCHLIST_WIDGET_TYPES = new Set(['watchlist', 'watchlistFlat']);
const DEFAULT_FLAT_WATCHLIST_COLUMNS = [84, 78, 62, 62, 28];

function resolveWidgetType(type) {
  return WIDGET_TYPE_ALIASES[type] || type;
}

function isWatchlistWidgetType(type) {
  return WATCHLIST_WIDGET_TYPES.has(resolveWidgetType(type));
}

function normalizeWatchlistWidgetSettings(settings) {
  const flatColumnWidths = Array.isArray(settings?.flatColumnWidths)
    ? DEFAULT_FLAT_WATCHLIST_COLUMNS.map((fallbackWidth, index) => {
        const width = Number(settings.flatColumnWidths[index]);
        return Number.isFinite(width) ? Math.max(28, Math.min(260, Math.round(width))) : fallbackWidth;
      })
    : DEFAULT_FLAT_WATCHLIST_COLUMNS;

  return {
    layoutMode: WATCHLIST_LAYOUT_MODES.has(settings?.layoutMode) ? settings.layoutMode : 'auto',
    flatColumnWidths,
  };
}

function getDefaultWidgetSettingsForType(type) {
  if (isWatchlistWidgetType(type)) return normalizeWatchlistWidgetSettings();
  return undefined;
}

function normalizeWidgetSettingsForType(type, settings) {
  if (isWatchlistWidgetType(type)) return normalizeWatchlistWidgetSettings(settings);
  if (settings && typeof settings === 'object' && !Array.isArray(settings)) return settings;
  return undefined;
}

function normalizeStudioState(layoutsInput = { lg: [] }, widgetTypesInput = {}, widgetSettingsInput = {}) {
  const widgetTypes = Object.fromEntries(
    Object.entries(widgetTypesInput || {}).map(([id, type]) => [id, resolveWidgetType(type)])
  );
  const normalizedWidgetSettings = Object.fromEntries(
    Object.entries(widgetTypes)
      .filter(([, type]) => widgetMeta[type])
      .map(([id, type]) => {
        const explicitSettings = normalizeWidgetSettingsForType(type, widgetSettingsInput?.[id]);
        return [id, explicitSettings ?? getDefaultWidgetSettingsForType(type)];
      })
      .filter(([, settings]) => settings !== undefined)
  );
  const originalLayouts = layoutsInput || { lg: [] };
  const normalizedLg = (originalLayouts.lg || []).map((item) => {
    const type = widgetTypes[item.i];
    const meta = type && widgetMeta[type];
    const minH = meta?.minH || 3;
    const minW = meta?.minW || 3;

    return {
      ...item,
      h: Math.max(item.h ?? minH, minH),
      w: Math.max(item.w ?? minW, minW),
      x: Math.max(0, Math.round(item.x || 0)),
      y: Math.max(0, Math.round(item.y || 0)),
      minH,
      minW,
    };
  });

  const existingIds = new Set(normalizedLg.map((item) => item.i));
  const missingItems = Object.entries(widgetTypes)
    .filter(([id, type]) => !existingIds.has(id) && widgetMeta[type])
    .map(([id, type]) => {
      const meta = widgetMeta[type];
      return {
        i: id,
        x: 0,
        y: Infinity,
        w: meta.defaultW,
        h: meta.defaultH,
        minH: meta.minH || 3,
        minW: meta.minW || 3,
      };
    });

  return {
    layouts: {
      ...originalLayouts,
      lg: [...normalizedLg, ...missingItems],
    },
    widgetTypes,
    widgetSettings: normalizedWidgetSettings,
  };
}

function getStoredIsStaffUser() {
  if (typeof window === 'undefined') {
    return false;
  }

  try {
    const stored = window.localStorage.getItem('user');
    return stored ? JSON.parse(stored)?.is_staff === true : false;
  } catch {
    return false;
  }
}

function getLayoutDisplayName(layout) {
  if (!layout?.name) {
    return 'Layouts';
  }

  return layout.is_global ? `${layout.name} (Global)` : layout.name;
}

function StudioView() {
  const location = useLocation();
  const navigate = useNavigate();
  const { accountId, symbol, timeframe, setTradeContext } = useTrade();
  const { openPositions, connectedAccountId, connect } = useAccountData();
  const { setRiskPercent } = useTradeState();
  const [showRecentTrades, setShowRecentTrades] = useState(false);

  useEffect(() => {
    if (accountId) connect(accountId);
  }, [accountId, connect]);

  useEffect(() => {
    const requestedAccountId = location.state?.accountId;
    const requestedSymbol = location.state?.symbol;
    const requestedTimeframe = location.state?.timeframe;

    if (!requestedAccountId && !requestedSymbol && !requestedTimeframe) {
      return;
    }

    const nextTradeContext = {};

    if (requestedAccountId && String(requestedAccountId) !== String(accountId ?? '')) {
      nextTradeContext.accountId = requestedAccountId;
    }

    if (requestedSymbol && requestedSymbol !== symbol) {
      nextTradeContext.symbol = requestedSymbol;
    }

    if (requestedTimeframe && requestedTimeframe !== timeframe) {
      nextTradeContext.timeframe = requestedTimeframe;
    }

    if (Object.keys(nextTradeContext).length > 0) {
      setTradeContext(nextTradeContext);
    }

    navigate(`${location.pathname}${location.search}`, { replace: true, state: null });
  }, [accountId, location.pathname, location.search, location.state, navigate, setTradeContext, symbol, timeframe]);

  // Grid sizing constants
  const ROW_HEIGHT = 30; // keep in sync with <ResponsiveGridLayout rowHeight>
  const GRID_MARGIN_Y = 10; // default RGL vertical margin
  const COLS = { lg: 64, md: 10, sm: 6, xs: 4, xxs: 2 };

  // --- Helpers for watchlist snapping ---
  const PILL_FALLBACK_H = 58;
  const GAP_FALLBACK = 8;

  // Generic helpers
  function snapToNearest(value, candidates = [], threshold = 1) {
    if (!Number.isFinite(value) || !Array.isArray(candidates) || candidates.length === 0) return value;
    let best = value;
    let bestDist = Infinity;
    for (const c of candidates) {
      if (!Number.isFinite(c)) continue;
      const d = Math.abs(c - value);
      if (d < bestDist) {
        bestDist = d;
        best = c;
      }
    }
    return bestDist <= threshold ? best : value;
  }

  function rectsCollide(a, b) {
    // a,b are grid items with x,y,w,h
    if (!a || !b) return false;
    return (
      a.x < b.x + b.w &&
      a.x + a.w > b.x &&
      a.y < b.y + b.h &&
      a.y + a.h > b.y
    );
  }

  function getWatchlistGridSteps({ el }) {
    if (!el) {
      return {
        rowsPerPillRow: Math.max(1, Math.round((PILL_FALLBACK_H + GAP_FALLBACK) / (ROW_HEIGHT + GRID_MARGIN_Y))),
        colsPerPill: 1,
        baseRows: 0,
      };
    }
    const pillsRoot = el.querySelector('.watchlist-pills');
    const body = el.querySelector('.widget-body');
    const pill = el.querySelector('.watchlist-pill, .watchlist-flat-row');
    const pillRect = pill?.getBoundingClientRect();
    const pillH = pillRect?.height || PILL_FALLBACK_H;
    let gapY = GAP_FALLBACK;
    if (pillsRoot) {
      const cs = window.getComputedStyle(pillsRoot);
      const rowGap = parseFloat(cs.rowGap || cs.gap || `${GAP_FALLBACK}`);
      if (Number.isFinite(rowGap)) gapY = rowGap;
    }
    const rowsPerPillRow = Math.max(1, Math.round((pillH + gapY) / (ROW_HEIGHT + GRID_MARGIN_Y)));
    const colsPerPill = 1;
    // Base header/padding rows so the top chrome + paddings never clip a pill row
    let baseRows = 0;
    try {
      if (body && pillsRoot) {
        const bodyRect = body.getBoundingClientRect();
        const pillsRect = pillsRoot.getBoundingClientRect();
        const topOffsetPx = Math.max(0, pillsRect.top - bodyRect.top);
        const bottomFudgePx = 6; // small guard to avoid shadow clipping
        baseRows = Math.ceil((topOffsetPx + bottomFudgePx) / (ROW_HEIGHT + GRID_MARGIN_Y));
      } else {
        // Conservative fallback for header + padding
        const fallbackBasePx = 40;
        baseRows = Math.ceil(fallbackBasePx / (ROW_HEIGHT + GRID_MARGIN_Y));
      }
    } catch {
      baseRows = 0;
    }
    return { rowsPerPillRow, colsPerPill, baseRows };
  }

  function snapToMultiple(value, step, min = step) {
    if (!Number.isFinite(value) || !Number.isFinite(step) || step <= 0) return min;
    const rem = value % step;
    const snapped = rem >= step / 2 ? value + (step - rem) : value - rem;
    return Math.max(min, snapped);
  }

  // Watchlist
  const [watchlist, setWatchlist] = useState([]);

  // Grid state
  const [layouts, setLayouts] = useState({ lg: [] });
  const [widgetTypes, setWidgetTypes] = useState({});
  const [widgetSettings, setWidgetSettings] = useState({});
  // Per-widget content size (scaling factor)
  const [widgetContentSizes, setWidgetContentSizes] = useState({});
  // Refs to widget DOM nodes for measurement
  const itemRefs = useRef({});

  // Interaction options
  const [magnetEnabled, setMagnetEnabled] = useState(true);
  const [noOverlap, setNoOverlap] = useState(false); // maps to preventCollision - false allows pushing widgets

  // Layout persistence
  const [savedLayouts, setSavedLayouts] = useState([]);
  const [selectedLayout, setSelectedLayout] = useState(null);
  // replaced always-visible input with modal-driven Save As / Rename
  const [newLayoutName, setNewLayoutName] = useState('');

  // NEW: UI/UX state
  const [isDirty, setIsDirty] = useState(false);
  const [showSaveAs, setShowSaveAs] = useState(false);
  const [showRename, setShowRename] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [saveAsGlobal, setSaveAsGlobal] = useState(false);
  const [showWidgetPicker, setShowWidgetPicker] = useState(false);
  const [editing, setEditing] = useState(false);
  const [breakpoint, setBreakpoint] = useState('lg');
  const [userProfile, setUserProfile] = useState(null);
  // Local persistence keys
  const LS_LAST_LAYOUT_ID = 'studio:lastLayoutId';
  const LS_LAST_SNAPSHOT = 'studio:lastLayoutSnapshot_v1';
  const didAutoRestoreRef = useRef(false);
  const userInteractingRef = useRef(false); // Track if user is dragging/resizing
  const committedLayoutsRef = useRef(null); // Track user-committed layouts (not affected by async state)

  const isStaff = useMemo(() => getStoredIsStaffUser() || userProfile?.is_staff === true, [userProfile]);
  const canManageSelectedLayout = Boolean(selectedLayout) && (!selectedLayout.is_global || isStaff);

  // Detect if any Watchlist widget exists in the layout to conditionally hide Symbol dropdown
  const hasWatchlist = useMemo(() => {
    try {
      return Object.values(widgetTypes || {}).some((t) => isWatchlistWidgetType(t));
    } catch {
      return false;
    }
  }, [widgetTypes]);

  // 🔹 Lifted shared trade/chart state
  const [pipSize, setPipSize] = useState(null);
  const [tickSize, setTickSize] = useState(null);
  const [contractSize, setContractSize] = useState(null);
  const [digits, setDigits] = useState(undefined);
  const [stopLossPrice, setStopLossPrice] = useState('');   // share across TradePanel + Chart
  const [takeProfitPrice, setTakeProfitPrice] = useState(''); // share across TradePanel + Chart

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    let cancelled = false;

    const loadUserProfile = () => {
      getUserProfile()
        .then((profile) => {
          if (!cancelled) {
            setUserProfile(profile || null);
          }
        })
        .catch(() => {
          if (!cancelled) {
            setUserProfile(null);
          }
        });
    };

    loadUserProfile();
    window.addEventListener('paxia:profile-updated', loadUserProfile);

    return () => {
      cancelled = true;
      window.removeEventListener('paxia:profile-updated', loadUserProfile);
    };
  }, []);

  // Fetch layouts + watchlist on mount
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const response = await listWatchlistItems();
        // Handle paginated response format {count, next, previous, results}
        const items = response?.results || response;
        setWatchlist(Array.isArray(items) ? items : []);
      } catch {
        setWatchlist([]);
      }
      // Fetch saved layouts, then try to auto-restore last visit
  const list = await fetchLayouts();
      if (!didAutoRestoreRef.current) {
        didAutoRestoreRef.current = true;
        try {
          const lastId = localStorage.getItem(LS_LAST_LAYOUT_ID);
          if (lastId && Array.isArray(list)) {
            const exists = list.find((l) => String(l.id) === String(lastId));
            if (exists) {
              handleSelectLayout(exists.id, list);
              return;
            }
          }
          // Fallback to a local snapshot if no lastId match
          const raw = localStorage.getItem(LS_LAST_SNAPSHOT);
          if (raw) {
            const snap = JSON.parse(raw);
            if (snap && snap.layouts && snap.widget_types) {
              const normalized = normalizeStudioState(snap.layouts, snap.widget_types, snap.widget_settings);
              setLayouts(normalized.layouts);
              committedLayoutsRef.current = normalized.layouts;
              setWidgetTypes(normalized.widgetTypes);
              setWidgetSettings(normalized.widgetSettings);
            } else {
              // initialize a minimal snapshot to avoid future blanks
              localStorage.setItem(LS_LAST_SNAPSHOT, JSON.stringify({ layouts: { lg: [] }, widget_types: {}, widget_settings: {} }));
            }
          }
        } catch {
          // ignore restore errors
        }
      }
    };
    fetchInitialData();
    // handleSelectLayout is intentionally used only for first-run restore.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchLayouts = async () => {
    try {
      const response = await listStudioLayouts();
      // Handle paginated response format {count, next, previous, results}
      const layouts = response?.results || response;
      const layoutsArray = Array.isArray(layouts)
        ? layouts.map((layout) => {
            const normalized = normalizeStudioState(layout.layouts, layout.widget_types, layout.widget_settings);
            return {
              ...layout,
              layouts: normalized.layouts,
              widget_types: normalized.widgetTypes,
              widget_settings: normalized.widgetSettings,
            };
          })
        : [];
      setSavedLayouts(layoutsArray);
      return layoutsArray;
    } catch (error) {
      console.error('Failed to fetch layouts:', error);
      return [];
    }
  };

  // NEW: track dirty state whenever grid or widget types change vs currently selected layout
  useEffect(() => {
    if (!selectedLayout) {
      setIsDirty(
        (layouts?.lg?.length || 0) > 0 ||
        Object.keys(widgetTypes).length > 0 ||
        Object.keys(widgetSettings).length > 0
      );
      return;
    }
    try {
      const a = JSON.stringify({ layouts, widget_types: widgetTypes, widget_settings: widgetSettings });
      const b = JSON.stringify({
        layouts: selectedLayout.layouts,
        widget_types: selectedLayout.widget_types,
        widget_settings: selectedLayout.widget_settings || {},
      });
      setIsDirty(a !== b);
    } catch {
      setIsDirty(true);
    }
  }, [layouts, widgetTypes, widgetSettings, selectedLayout]);

  // 🔹 Fetch symbol info when account or symbol changes
  useEffect(() => {
    // Reset for the new instrument before fetching metadata. A slow request must
    // not erase values entered after the context has already changed.
    setStopLossPrice('');
    setTakeProfitPrice('');
    let cancelled = false;
    async function run() {
      if (!accountId || !symbol) return;
      try {
        const info = await getSymbolInfo(accountId, symbol);
        if (!cancelled && info) {
          setPipSize(info.pip_size ?? null);
          setTickSize(info.tick_size ?? null);
          setContractSize(info.contract_size ?? null);
          if (typeof info.digits === 'number') {
            setDigits(info.digits);
            setDigitsFor(accountId, symbol, info.digits);
          } else {
            setDigits(undefined);
          }
        }
      } catch (e) {
        console.error('Failed to fetch symbol info:', e);
        if (!cancelled) {
          setPipSize(null);
          setTickSize(null);
          setContractSize(null);
          setDigits(undefined);
        }
      }
    }
    run();
    return () => { cancelled = true; };
  }, [accountId, symbol]);

  // Grid change — accept RGL's layout but enforce minW/minH on every item.
  // During active drag/resize, skip (onDragStop/onResizeStop handle final commit).
  const onLayoutChange = (layout, newLayouts) => {
    if (userInteractingRef.current) return;

    setLayouts(prev => {
      const bp = breakpoint || 'lg';
      const rglItems = newLayouts?.[bp] || layout || [];
      const prevItems = prev?.[bp] || [];

      // Merge: accept x/y from RGL compaction, enforce w >= minW, h >= minH
      const merged = rglItems.map(rglItem => {
        const existing = prevItems.find(p => p.i === rglItem.i);
        const type = widgetTypes[rglItem.i];
        const meta = type && widgetMeta[type];
        const mW = meta?.minW || existing?.minW || rglItem.minW || 3;
        const mH = meta?.minH || existing?.minH || rglItem.minH || 3;
        return {
          ...(existing || rglItem),
          x: rglItem.x,
          y: rglItem.y,
          w: Math.max(existing?.w ?? rglItem.w, mW),
          h: Math.max(existing?.h ?? rglItem.h, mH),
          minW: mW,
          minH: mH,
        };
      });

      // Bail out (same reference → no re-render) if nothing meaningful changed
      if (prevItems.length === merged.length &&
          prevItems.every((p, i) =>
            p.i === merged[i].i && p.x === merged[i].x && p.y === merged[i].y &&
            p.w === merged[i].w && p.h === merged[i].h)) {
        return prev;
      }

      const result = { ...prev, [bp]: merged };
      committedLayoutsRef.current = result;
      return result;
    });
  };
  // Ensure no NaN in layout h/w and enforce w >= minW, h >= minH
  const sanitizeItems = (arr = []) => arr.map(it => {
    const toInt = (v, min = 0) => (Number.isFinite(v) ? Math.max(min, Math.round(v)) : min);
    const type = widgetTypes[it.i];
    const meta = type && widgetMeta[type];
    const mW = meta?.minW || it.minW || 3;
    const mH = meta?.minH || it.minH || 3;
    return {
      ...it,
      x: toInt(it.x, 0),
      y: toInt(it.y, 0),
      w: Math.max(toInt(it.w, 1), mW),
      h: Math.max(toInt(it.h, 1), mH),
      minW: mW,
      minH: mH,
    };
  });
  const safeSetLayouts = (updater) => {
    setLayouts(prev => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      const result = { ...next };
      for (const bp of ['lg','md','sm','xs','xxs']) {
        if (Array.isArray(result[bp])) result[bp] = sanitizeItems(result[bp]);
      }
      return result;
    });
  };

  useEffect(() => {
    setLayouts(prev => {
      let changed = false;
      const next = { ...prev };
      for (const bp of ['lg','md','sm','xs','xxs']) {
        if (!Array.isArray(next[bp])) continue;
        next[bp] = next[bp].map((item) => {
          const type = resolveWidgetType(widgetTypes[item.i]);
          const meta = type && widgetMeta[type];
          if (!meta || item.minH === meta.minH) return item;
          changed = true;
          return { ...item, minH: meta.minH };
        });
      }
      if (!changed) return prev;
      committedLayoutsRef.current = next;
      return next;
    });
  }, [widgetTypes]);

  const openSaveAsModal = (suggestedName = '') => {
    setNewLayoutName(suggestedName);
    setSaveAsGlobal(isStaff && Boolean(selectedLayout?.is_global));
    setShowSaveAs(true);
  };

  const closeSaveAsModal = () => {
    setShowSaveAs(false);
    setNewLayoutName('');
    setSaveAsGlobal(false);
  };

  // Save / Load / Delete layouts
  const handleSaveLayout = async () => {
    // Save current content into selected layout or open Save As if none is chosen
    if (!selectedLayout) {
      openSaveAsModal('');
      return;
    }
    if (!canManageSelectedLayout) {
      openSaveAsModal(selectedLayout?.name ? `${selectedLayout.name} copy` : '');
      return;
    }
    // Use committedLayoutsRef if available (contains user's actual drag/resize positions)
    const normalized = normalizeStudioState(committedLayoutsRef.current || layouts, widgetTypes, widgetSettings);
    const layoutsToSave = normalized.layouts;
    const layoutData = {
      name: selectedLayout.name,
      layouts: layoutsToSave,
      widget_types: normalized.widgetTypes,
      widget_settings: normalized.widgetSettings,
      is_global: selectedLayout.is_global === true,
    };
    console.log('=== SAVE ===');
    console.log('Saving layouts:', JSON.stringify(layoutsToSave?.lg?.map(i => ({i: i.i, x: i.x, y: i.y, w: i.w, h: i.h}))));
    try {
      await updateStudioLayout(selectedLayout.id, layoutData);
      // refresh list and update selectedLayout snapshot with fresh data
      const freshLayouts = await fetchLayouts();
      // Update selectedLayout with the saved data to reset dirty state
      const updatedLayout = freshLayouts.find(l => l.id === selectedLayout.id);
      if (updatedLayout) {
        setSelectedLayout(updatedLayout);
      } else {
        // If not found in list, manually update the snapshot
        setSelectedLayout({
          ...selectedLayout,
          layouts: normalized.layouts,
          widget_types: normalized.widgetTypes,
          widget_settings: normalized.widgetSettings,
        });
      }
    } catch (error) {
      console.error('Failed to save layout:', error);
    }
  };

  const handleSaveAsLayout = async () => {
    const trimmedName = newLayoutName.trim();
    if (!trimmedName) return;
    const normalized = normalizeStudioState(committedLayoutsRef.current || layouts, widgetTypes, widgetSettings);
    const layoutData = {
      name: trimmedName,
      layouts: normalized.layouts,
      widget_types: normalized.widgetTypes,
      widget_settings: normalized.widgetSettings,
      is_global: isStaff ? saveAsGlobal : false,
    };
    try {
      const newLayout = await createStudioLayout(layoutData);
      closeSaveAsModal();
      const freshLayouts = await fetchLayouts();
      // Select the newly created layout
      const createdLayoutId = newLayout?.id || newLayout?.data?.id;
      const createdLayout = freshLayouts.find((layout) => String(layout.id) === String(createdLayoutId))
        || freshLayouts.find((layout) => layout.name === trimmedName && layout.is_global === (isStaff ? saveAsGlobal : false))
        || newLayout?.data
        || newLayout;
      if (createdLayout?.id) {
        setSelectedLayout(createdLayout);
        try { localStorage.setItem(LS_LAST_LAYOUT_ID, String(createdLayout.id)); } catch { /* ignore local persistence errors */ }
      }
    } catch (error) {
      console.error('Failed to create layout:', error);
    }
  };

  const handleRenameLayout = async () => {
    const trimmedName = newLayoutName.trim();
    if (!selectedLayout || !trimmedName || !canManageSelectedLayout) return;
    try {
      // keep content unchanged; only rename
      await updateStudioLayout(selectedLayout.id, {
        name: trimmedName,
        layouts: selectedLayout.layouts,
        widget_types: selectedLayout.widget_types,
        widget_settings: selectedLayout.widget_settings || {},
        is_global: selectedLayout.is_global === true,
      });
      setShowRename(false);
      setNewLayoutName('');
      const freshLayouts = await fetchLayouts();
      // Update selectedLayout with renamed data
      const updatedLayout = freshLayouts.find(l => l.id === selectedLayout.id);
      if (updatedLayout) {
        setSelectedLayout(updatedLayout);
      }
    } catch (error) {
      console.error('Failed to rename layout:', error);
    }
  };

  const handleDeleteLayout = async () => {
    if (!selectedLayout || !canManageSelectedLayout) return;
    try {
      await deleteStudioLayout(selectedLayout.id);
      setSelectedLayout(null);
      try { localStorage.removeItem(LS_LAST_LAYOUT_ID); } catch { /* ignore local persistence errors */ }
      setShowDeleteConfirm(false);
      await fetchLayouts();
    } catch (error) {
      console.error('Failed to delete layout:', error);
    }
  };

  // Auto-load when selection changes
  const handleSelectLayout = (layoutId, listOverride = null) => {
    const source = Array.isArray(listOverride) ? listOverride : savedLayouts;
    const layout = (source || []).find(l => String(l.id) === String(layoutId)) || null;
    try { localStorage.setItem(LS_LAST_LAYOUT_ID, String(layoutId)); } catch { /* ignore local persistence errors */ }
    if (layout) {
      // Normalize loaded layout to allow shrinking to 1x1
      try {
        const normalized = normalizeStudioState(layout.layouts, layout.widget_types, layout.widget_settings);
        setSelectedLayout({
          ...layout,
          layouts: normalized.layouts,
          widget_types: normalized.widgetTypes,
          widget_settings: normalized.widgetSettings,
        });
        setLayouts(normalized.layouts);
        committedLayoutsRef.current = normalized.layouts; // Initialize ref with loaded layout
        setWidgetTypes(normalized.widgetTypes);
        setWidgetSettings(normalized.widgetSettings);
      } catch {
        const normalized = normalizeStudioState(layout.layouts, layout.widget_types, layout.widget_settings);
        safeSetLayouts(normalized.layouts);
        committedLayoutsRef.current = normalized.layouts; // Initialize ref with loaded layout
        setWidgetTypes(normalized.widgetTypes);
        setWidgetSettings(normalized.widgetSettings);
        setSelectedLayout({
          ...layout,
          layouts: normalized.layouts,
          widget_types: normalized.widgetTypes,
          widget_settings: normalized.widgetSettings,
        });
      }
    } else {
      setSelectedLayout(null);
    }
  };

  // Add/remove widgets
  const addWidgetOfType = (type) => {
    if (!type) return;
    const id = `widget-${Date.now()}`;
    const meta = widgetMeta[type];
    // Defaults
    let w = meta.defaultW, h = meta.defaultH;
    const minW = meta.minW || 3;
    const minH = meta.minH || 3;
    const newItem = { i: id, x: 0, y: Infinity, w, h, minH, minW };
    safeSetLayouts(prev => {
      const next = { ...prev, lg: [...(prev.lg || []), newItem] };
      committedLayoutsRef.current = next;
      return next;
    });
    setWidgetTypes(prev => ({ ...prev, [id]: type }));
    const defaultSettings = getDefaultWidgetSettingsForType(type);
    if (defaultSettings !== undefined) {
      setWidgetSettings(prev => ({ ...prev, [id]: defaultSettings }));
    }
    setEditing(true);
  };

  const removeWidget = (id) => {
  safeSetLayouts(prev => {
    const next = { ...prev, lg: (prev.lg || []).filter(item => item.i !== id) };
    committedLayoutsRef.current = next;
    return next;
  });
    setWidgetTypes(prev => {
      const { [id]: _, ...rest } = prev;
      return rest;
    });
    setWidgetSettings(prev => {
      const { [id]: _removed, ...rest } = prev;
      return rest;
    });
  };

  // Persist a local snapshot of the current grid on changes (debounced)
  useEffect(() => {
    const t = setTimeout(() => {
      try {
        const normalized = normalizeStudioState(committedLayoutsRef.current || layouts, widgetTypes, widgetSettings);
        const payload = JSON.stringify({
          layouts: normalized.layouts,
          widget_types: normalized.widgetTypes,
          widget_settings: normalized.widgetSettings,
        });
        localStorage.setItem(LS_LAST_SNAPSHOT, payload);
      } catch { /* ignore local persistence errors */ }
    }, 300);
    return () => clearTimeout(t);
  }, [layouts, widgetTypes, widgetSettings]);

  // Render widget content (inject lifted state where needed)
  const renderWidgetContent = (id) => {
    const type = resolveWidgetType(widgetTypes[id]);
    const meta = widgetMeta[type];
    if (!meta) {
      return (
        <div className="flex h-full items-center justify-center text-sm text-textSecondary">
          Widget unavailable
        </div>
      );
    }
    const contentSize = widgetContentSizes[id] ?? 1.0;
    const currentWidgetSettings = widgetSettings[id] || getDefaultWidgetSettingsForType(type);
    const setWidgetSetting = (nextPartial) => {
      setWidgetSettings((prev) => {
        const previousValue = prev[id] || getDefaultWidgetSettingsForType(type) || {};
        const mergedValue = typeof nextPartial === 'function'
          ? nextPartial(previousValue)
          : { ...previousValue, ...(nextPartial || {}) };
        const normalizedValue = normalizeWidgetSettingsForType(type, mergedValue);
        if (normalizedValue === undefined) return prev;
        return { ...prev, [id]: normalizedValue };
      });
    };
    return meta.render({
      accountId,
      widgetSettings: currentWidgetSettings,
      setWidgetSetting,
      isCustomizing: editing,
      symbol,
      timeframe,
      pipSize,
      tickSize,
      digits,
      contractSize,
      stopLossPrice,
      setStopLossPrice,
      takeProfitPrice,
      setTakeProfitPrice,
      openPositions: String(connectedAccountId ?? '') === String(accountId ?? '') ? openPositions : [],
      setTradeContext,
      contentSize,
  showRecentTrades,
  onToggleRecentTrades: () => setShowRecentTrades(v => !v),
    });
  };

  // Measure a widget's content and adjust its grid height (rows)
  const resizeItemToContent = (id) => {
    const root = itemRefs.current[id];
    if (!root) return;
    const body = root.querySelector('.widget-body');
    const contentHeight = body?.scrollHeight || root.scrollHeight;
    // Convert px -> rows taking vertical margins into account
    const rows = Math.max(1, Math.ceil((contentHeight + GRID_MARGIN_Y) / (ROW_HEIGHT + GRID_MARGIN_Y)));
  safeSetLayouts(prev => {
      const current = prev?.lg || [];
      const idx = current.findIndex(it => it.i === id);
      if (idx === -1) return prev;
      const item = current[idx];
      // Only auto-grow; don't shrink below user's current size
      if (rows <= item.h) return prev;
      const updated = [...current];
      updated[idx] = { ...item, h: rows };
      return { ...prev, lg: updated };
    });
  };

  // Observe tradePanel widgets to auto-fit their height to content
  useEffect(() => {
    const observers = [];
    const ids = Object.keys(widgetTypes).filter(id => widgetTypes[id] === 'tradePanel');
    ids.forEach(id => {
      const root = itemRefs.current[id];
      const body = root?.querySelector('.widget-body');
      if (!body) return;
      const ro = new ResizeObserver(() => resizeItemToContent(id));
      ro.observe(body);
      observers.push(ro);
      // initial measure
      resizeItemToContent(id);
    });
    return () => observers.forEach(o => o.disconnect());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [widgetTypes, selectedLayout, layouts.lg.length]);

  // Helper to export JSON
  const handleExportJson = () => {
    const data = JSON.stringify({
      name: selectedLayout?.name || 'Untitled',
      layouts,
      widget_types: widgetTypes,
      widget_settings: widgetSettings,
    }, null, 2);
    const blob = new Blob([data], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${selectedLayout?.name || 'layout'}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
  <div className="p-0.5 sm:p-1 md:p-4 bg-background overflow-x-hidden">

        {/* Studio Toolbar */}
        <div className="mb-2 sm:mb-3">
          <div
            className="flex flex-wrap items-center justify-between gap-1.5 sm:gap-2 rounded-lg bg-backgroundSurface/30 backdrop-blur-sm px-1.5 py-1.5 sm:px-3 sm:py-2"
            style={{ borderWidth: 'var(--card-border-width)', borderColor: 'var(--color-border-default)', borderStyle: 'solid' }}
          >
            {/* Left: Context selectors */}
            <div className="flex items-center gap-1.5 sm:gap-3 min-w-0 flex-1">
              {/* Account selector */}
              <div className="flex min-w-0 items-center gap-2">
                <span className="shrink-0 text-[10px] font-medium uppercase tracking-[0.08em] text-textSecondary sm:text-[11px]">
                  Account
                </span>
                <AccountSelection
                  selectedAccount={accountId}
                  setSelectedAccount={(newAccountId) => setTradeContext({ accountId: newAccountId })}
                  setRiskPercentFromSettings={setRiskPercent}
                />
              </div>
              {!hasWatchlist && (
                <>
                  <div className="hidden sm:block w-px h-5 bg-borderDefault/40" />
                  <div className="min-w-0 sm:min-w-[180px]">
                    <SymbolSelection
                      selectedSymbol={symbol}
                      setSelectedSymbol={(newSymbol) => setTradeContext({ symbol: newSymbol })}
                      watchlist={watchlist}
                      accountId={accountId}
                    />
                  </div>
                </>
              )}
            </div>

            {/* Right: Grid controls */}
            <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
              <div className="shrink-0">
                <Select
                  value={selectedLayout?.id || ''}
                  onValueChange={(v) => {
                    if (typeof v === 'string' && v.startsWith('__action_')) {
                      switch (v) {
                        case '__action_save':
                          handleSaveLayout();
                          break;
                        case '__action_save_as':
                          openSaveAsModal(selectedLayout?.name ? `${selectedLayout.name} copy` : '');
                          break;
                        case '__action_rename':
                          if (canManageSelectedLayout) {
                            setNewLayoutName(selectedLayout.name || '');
                            setShowRename(true);
                          }
                          break;
                        case '__action_export':
                          handleExportJson();
                          break;
                        case '__action_delete':
                          if (canManageSelectedLayout) setShowDeleteConfirm(true);
                          break;
                        default:
                          break;
                      }
                      return;
                    }
                    if (typeof v === 'string' && v.startsWith('__')) return;
                    handleSelectLayout(v);
                  }}
                >
                  <SelectTrigger className="w-[84px] sm:w-[200px] text-xs sm:text-sm truncate">{selectedLayout ? getLayoutDisplayName(selectedLayout) : 'Layouts'}</SelectTrigger>
                  <SelectContent>
                    {(savedLayouts && savedLayouts.length > 0) ? (
                      savedLayouts.map((l) => (
                        <SelectItem key={l.id} value={String(l.id)}>{getLayoutDisplayName(l)}</SelectItem>
                      ))
                    ) : (
                      <SelectItem value="" disabled>No layouts</SelectItem>
                    )}
                    <SelectItem value="__sep" disabled>— Actions —</SelectItem>
                    <SelectItem value="__action_save">{selectedLayout?.is_global && !canManageSelectedLayout ? 'Save Copy…' : `Save${isDirty ? ' •' : ''}`}</SelectItem>
                    <SelectItem value="__action_save_as">Save As…</SelectItem>
                    <SelectItem value="__action_rename" disabled={!canManageSelectedLayout}>Rename…</SelectItem>
                    <SelectItem value="__action_export">Export JSON</SelectItem>
                    <SelectItem value="__action_delete" disabled={!canManageSelectedLayout}>Delete</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="hidden sm:block w-px h-5 bg-borderDefault/40" />

              {/* Magnet toggle – icon button, hidden on mobile */}
              <button
                onClick={() => setMagnetEnabled(v => !v)}
                title={editing ? `Snap to grid: ${magnetEnabled ? 'On' : 'Off'}` : 'Available in Customize mode'}
                disabled={!editing}
                className={[
                  'hidden sm:inline-flex items-center justify-center rounded-md p-1.5 transition-colors disabled:cursor-not-allowed disabled:opacity-50',
                  editing && magnetEnabled
                    ? 'bg-primary/15 text-primary hover:bg-primary/25'
                    : 'text-textSecondary hover:text-textPrimary hover:bg-secondary/15',
                ].join(' ')}
              >
                <Magnet className="h-4 w-4" />
              </button>

              {/* Lock toggle – icon button, hidden on mobile */}
              <button
                onClick={() => setNoOverlap(v => !v)}
                title={editing ? `Lock layout: ${noOverlap ? 'On' : 'Off'}` : 'Available in Customize mode'}
                disabled={!editing}
                className={[
                  'hidden sm:inline-flex items-center justify-center rounded-md p-1.5 transition-colors disabled:cursor-not-allowed disabled:opacity-50',
                  editing && noOverlap
                    ? 'bg-primary/15 text-primary hover:bg-primary/25'
                    : 'text-textSecondary hover:text-textPrimary hover:bg-secondary/15',
                ].join(' ')}
              >
                {noOverlap ? <Lock className="h-4 w-4" /> : <LockOpen className="h-4 w-4" />}
              </button>

              {/* Separator */}
              <div className="hidden sm:block w-px h-5 bg-borderDefault/40" />

              <Button
                size="sm"
                variant={editing ? 'secondary' : 'outline'}
                onClick={() => setEditing(currentValue => !currentValue)}
                className="gap-1.5 text-xs sm:text-sm px-2.5 sm:px-3"
              >
                <GripVertical className="h-3.5 w-3.5" />
                <span>{editing ? 'Done' : 'Customize'}</span>
              </Button>

              {/* Add Widget button */}
              <Button
                size="sm"
                onClick={() => setShowWidgetPicker(true)}
                className="gap-1.5 text-xs sm:text-sm px-2.5 sm:px-3"
              >
                <Plus className="h-3.5 w-3.5" />
                <span className="hidden sm:inline">Add Widget</span>
              </Button>
            </div>
          </div>
        </div>

        {selectedLayout?.is_global && !canManageSelectedLayout && (
          <div className="mb-2 rounded-lg border border-borderDefault bg-backgroundSurface/40 px-3 py-2 text-xs text-textSecondary">
            This is a global staff layout. You can load it and save a personal copy, but only staff can update or delete it.
          </div>
        )}

        {/* Optional: keep quick-add select hidden in header; still available if needed */}
        {/* <div className="hidden"> */}
        {/*   <div className="w-40 ml-4"> */}
        {/*     <CustomSelect options={widgetOptions} value={selectedWidget} onChange={setSelectedWidget} placeholder="Select widget" /> */}
        {/*   </div> */}
        {/*   <Button onClick={addWidget}>Add Widget</Button> */}
        {/* </div> */}

  {/* Account/Symbol moved into toolbar above */}

        {/* Grid */}
        <ResponsiveGridLayout
          style={{ minHeight: '60vh' }}
          className="layout"
          layouts={layouts}
          onLayoutChange={onLayoutChange}
          onBreakpointChange={(bp) => setBreakpoint(bp)}
          onDrag={(_layout, _oldItem, newItem) => {
            // Apply optional magnet snapping and reduce jitter near edges
            if (!magnetEnabled) return;
            safeSetLayouts(prev => {
              const bp = breakpoint || 'lg';
              const arr = Array.isArray(prev?.[bp]) ? [...prev[bp]] : [];
              const idx = arr.findIndex(it => it.i === newItem.i);
              if (idx === -1) return prev;
              const moving = { ...arr[idx], x: Math.max(0, Math.round(newItem.x)), y: Math.max(0, Math.round(newItem.y)) };
              const others = arr.filter((it, j) => j !== idx);

              // Build candidate snap lines from neighbors (left/right/top/bottom edges)
              const snapXs = [];
              const snapYs = [];
              for (const it of others) {
                snapXs.push(it.x, it.x + it.w);
                snapYs.push(it.y, it.y + it.h);
              }

              // Thresholds: 1 grid unit (~row/col)
              const xSnap = snapToNearest(moving.x, snapXs, 1);
              const ySnap = snapToNearest(moving.y, snapYs, 1);

              // Prefer snapping that doesn't create overlap; if overlap, keep original
              const tryPositions = [
                { x: xSnap, y: moving.y },
                { x: moving.x, y: ySnap },
                { x: xSnap, y: ySnap },
                { x: moving.x, y: moving.y },
              ];
              let chosen = moving;
              for (const pos of tryPositions) {
                const temp = { ...moving, ...pos };
                const overlapping = others.some(o => rectsCollide(temp, o));
                if (!overlapping) { chosen = temp; break; }
              }

              arr[idx] = { ...arr[idx], x: chosen.x, y: chosen.y };
              return { ...prev, [bp]: arr };
            });
          }}
          onResize={(layout, oldItem, newItem) => {
            // Live snapping feedback during resize
            safeSetLayouts(prev => {
              const bp = breakpoint || 'lg';
              const type = widgetTypes[newItem.i];
              if (!isWatchlistWidgetType(type)) return prev;
              const toInt = (v, min = 0) => (Number.isFinite(v) ? Math.max(min, Math.round(v)) : min);
              let hSnap = toInt(newItem.h, 1);
              let wSnap = toInt(newItem.w, 1);
              const el = itemRefs.current[newItem.i];
              const { rowsPerPillRow, colsPerPill, baseRows } = getWatchlistGridSteps({ el });
              const innerH = Math.max(0, hSnap - baseRows);
              const snappedInnerH = snapToMultiple(innerH, rowsPerPillRow, rowsPerPillRow);
              hSnap = baseRows + snappedInnerH;
              wSnap = snapToMultiple(wSnap, colsPerPill, colsPerPill);

              const arr = (prev?.[bp] || []).map(it => it.i === newItem.i ? { ...it, w: wSnap, h: hSnap } : it);
              return { ...prev, [bp]: arr };
            });
          }}
          breakpoints={{ lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 }}
          cols={COLS}
          rowHeight={ROW_HEIGHT}
          compactType="vertical"
          preventCollision={false}
          isDraggable={editing && breakpoint !== 'xs' && breakpoint !== 'xxs'}
          isResizable={editing && breakpoint !== 'xs' && breakpoint !== 'xxs'}
          resizeHandles={["s", "w", "e", "n", "sw", "nw", "se", "ne"]}
          draggableHandle=".drag-handle"
          draggableCancel=".no-drag, button, a, input, select, textarea, [role='menu'], [role='menuitem'], [data-interactive='true']"
          onDragStart={() => { userInteractingRef.current = true; }}
          onDragStop={(layout, oldItem, newItem) => {
            userInteractingRef.current = false;
            if (!layout) return;
            const bp = breakpoint || 'lg';

            // 1) Magnet snapping for the dragged widget
            if (magnetEnabled && newItem) {
              const arr = Array.isArray(layouts?.[bp]) ? [...layouts[bp]] : [];
              const idx = arr.findIndex(it => it.i === newItem.i);
              if (idx !== -1) {
                const moving = { ...arr[idx], x: Math.max(0, Math.round(newItem.x)), y: Math.max(0, Math.round(newItem.y)) };
                const others = arr.filter((_, j) => j !== idx);
                const snapXs = [];
                const snapYs = [];
                for (const it of others) {
                  snapXs.push(it.x, it.x + it.w);
                  snapYs.push(it.y, it.y + it.h);
                }
                const xSnap = snapToNearest(moving.x, snapXs, 1);
                const ySnap = snapToNearest(moving.y, snapYs, 1);
                const tryPositions = [
                  { x: xSnap, y: moving.y },
                  { x: moving.x, y: ySnap },
                  { x: xSnap, y: ySnap },
                  { x: moving.x, y: moving.y },
                ];
                let chosen = moving;
                for (const pos of tryPositions) {
                  const temp = { ...moving, ...pos };
                  if (!others.some(o => rectsCollide(temp, o))) { chosen = temp; break; }
                }
                // Update the dragged item in the RGL layout array so the ref merge below uses snapped pos
                const draggedInLayout = layout.find(l => l.i === newItem.i);
                if (draggedInLayout) { draggedInLayout.x = chosen.x; draggedInLayout.y = chosen.y; }
              }
            }

            // 2) Merge into committed ref, preserving h/w from our committed state
            const currentLayouts = committedLayoutsRef.current || layouts;
            const existing = currentLayouts?.[bp] || [];
            const merged = existing.map(item => {
              const updated = layout.find(l => l.i === item.i);
              if (updated) {
                return { ...item, x: updated.x, y: updated.y };
                // Keep our committed w/h — RGL drag should not change sizes
              }
              return item;
            });
            committedLayoutsRef.current = { ...currentLayouts, [bp]: merged };
            setLayouts(committedLayoutsRef.current);
          }}
          onResizeStart={() => { userInteractingRef.current = true; }}
          onResizeStop={(layout, oldItem, newItem) => {
            userInteractingRef.current = false;

            // 1) Apply snapping for the resized widget (watchlist pill snap etc.)
            const bp = breakpoint || 'lg';
            const toInt = (v, min = 0) => (Number.isFinite(v) ? Math.max(min, Math.round(v)) : min);
            const type = widgetTypes[newItem.i];
            let snappedH = toInt(newItem.h, 1);
            let snappedW = toInt(newItem.w, 1);
            let minWStep = 1;
            let minHStep = 1;
            if (isWatchlistWidgetType(type)) {
              const el = itemRefs.current[newItem.i];
              const { rowsPerPillRow, colsPerPill, baseRows } = getWatchlistGridSteps({ el });
              const innerH = Math.max(0, snappedH - baseRows);
              const snappedInnerH = snapToMultiple(innerH, rowsPerPillRow, rowsPerPillRow);
              snappedH = baseRows + snappedInnerH;
              snappedW = snapToMultiple(snappedW, colsPerPill, colsPerPill);
              minWStep = colsPerPill;
              minHStep = baseRows + rowsPerPillRow;
            }

            // 2) Merge ALL items from the RGL layout, preserving committed h/w for non-resized items
            const currentLayouts = committedLayoutsRef.current || layouts;
            const existing = currentLayouts?.[bp] || [];
            const merged = existing.map(item => {
              if (item.i === newItem.i) {
                // The resized widget: use snapped values
                return {
                  ...item,
                  w: snappedW,
                  h: snappedH,
                  x: toInt(newItem.x, 0),
                  y: toInt(newItem.y, 0),
                  minW: Math.max(toInt(item.minW, 1), minWStep),
                  minH: isWatchlistWidgetType(type) ? minHStep : Math.max(toInt(item.minH, 1), minHStep),
                };
              }
              // Other widgets: take updated position from RGL layout but KEEP our committed h/w
              const rglItem = layout.find(l => l.i === item.i);
              if (rglItem) {
                return {
                  ...item,
                  x: toInt(rglItem.x, 0),
                  y: toInt(rglItem.y, 0),
                  // preserve our h/w — do NOT take RGL's compacted values
                };
              }
              return item;
            });
            committedLayoutsRef.current = { ...currentLayouts, [bp]: merged };
            setLayouts(committedLayoutsRef.current);
          }}
        >
          {Array.from(new Set([...(layouts.lg || []).map(it => it.i), ...Object.keys(widgetTypes)])).map(id => {
            const type = resolveWidgetType(widgetTypes[id]);
            const baseLabel = widgetMeta[type]?.label || type || 'Unknown widget';
            // For chart widget, append the selected symbol name
            const label = type === 'chart' && symbol ? `${baseLabel} - ${symbol}` : baseLabel;
            const isWatchlist = isWatchlistWidgetType(type);
            return (
              <div key={id} className="h-full overflow-visible" ref={(el) => { if (el) itemRefs.current[id] = el; }}>
                <Card
                  className="relative flex flex-col h-full"
                  style={{ transform: `scale(${widgetContentSizes[id] ?? 1.0})`, transformOrigin: 'top left', transition: 'transform 0.2s' }}
                >
                  {/* Widget header bar with drag handle and close button */}
                  {!isWatchlist && (
                    <div className="flex items-center justify-between px-2 py-1.5 border-b border-borderDefault bg-backgroundSurface/50 shrink-0">
                      <div className="flex items-center gap-2">
                        {editing && breakpoint !== 'xs' && breakpoint !== 'xxs' && (
                          <div
                            className="drag-handle cursor-grab active:cursor-grabbing text-textSecondary hover:text-textPrimary p-0.5 rounded"
                            title={`Move ${label}`}
                          >
                            <GripVertical className="h-4 w-4" />
                          </div>
                        )}
                        <span className="text-sm font-medium text-textPrimary select-none">{label}</span>
                      </div>
                      {editing && (
                        <button
                          onClick={() => removeWidget(id)}
                          className="text-textSecondary hover:text-destructive p-0.5 rounded no-drag"
                          title="Remove widget"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4">
                            <path fillRule="evenodd" d="M10 8.586l3.536-3.536 1.414 1.414L11.414 10l3.536 3.536-1.414 1.414L10 11.414l-3.536 3.536-1.414-1.414L8.586 10 5.05 6.464l1.414-1.414L10 8.586z" clipRule="evenodd" />
                          </svg>
                        </button>
                      )}
                    </div>
                  )}

                  {isWatchlist && editing && (
                    <div className="absolute right-2 top-2 z-20 flex items-center gap-1 rounded-full border border-borderDefault bg-backgroundSurface/80 px-1.5 py-1 shadow-sm backdrop-blur">
                      {breakpoint !== 'xs' && breakpoint !== 'xxs' && (
                        <div
                          className="drag-handle cursor-grab active:cursor-grabbing text-textSecondary hover:text-textPrimary p-0.5 rounded"
                          title={`Move ${label}`}
                        >
                          <GripVertical className="h-4 w-4" />
                        </div>
                      )}
                      <button
                        onClick={() => removeWidget(id)}
                        className="text-textSecondary hover:text-destructive p-0.5 rounded no-drag"
                        title="Remove widget"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4">
                          <path fillRule="evenodd" d="M10 8.586l3.536-3.536 1.414 1.414L11.414 10l3.536 3.536-1.414 1.414L10 11.414l-3.536 3.536-1.414-1.414L8.586 10 5.05 6.464l1.414-1.414L10 8.586z" clipRule="evenodd" />
                        </svg>
                      </button>
                    </div>
                  )}

                  {/* Content: remove outer padding for watchlist to keep pill grid edges exact */}
                  <CardContent className={`${isWatchlist ? 'p-0' : 'p-2'} flex-1 overflow-auto widget-body`}>
                    {renderWidgetContent(id)}
                  </CardContent>
                  
                  {/* -/+ content size buttons bottom right */}
                  {editing && (
                    <div className="absolute bottom-2 right-2 z-10 flex items-center gap-1">
                      <button
                        className="bg-background/70 rounded p-1 text-xs shadow-sm no-drag"
                        title="Decrease content size"
                        onClick={() => setWidgetContentSizes(sizes => ({ ...sizes, [id]: Math.max(0.5, (sizes[id] ?? 1.0) - 0.1) }))}
                      >
                        -
                      </button>
                      <span className="text-xs px-1 select-none">{(widgetContentSizes[id] ?? 1.0).toFixed(1)}x</span>
                      <button
                        className="bg-background/70 rounded p-1 text-xs shadow-sm no-drag"
                        title="Increase content size"
                        onClick={() => setWidgetContentSizes(sizes => ({ ...sizes, [id]: Math.min(2.0, (sizes[id] ?? 1.0) + 0.1) }))}
                      >
                        +
                      </button>
                    </div>
                  )}
                </Card>
              </div>
            );
          })}
        </ResponsiveGridLayout>

        {/* Save As modal */}
        <Modal open={showSaveAs} onClose={closeSaveAsModal} title="Save layout as…"
          footer={<>
            <Button variant="outline" onClick={closeSaveAsModal} size="sm">Cancel</Button>
            <Button onClick={handleSaveAsLayout} size="sm" disabled={!newLayoutName.trim()}>Save</Button>
          </>}
        >
          <div className="space-y-2">
            <label className="text-sm text-textSecondary">Layout name</label>
            <Input value={newLayoutName} onChange={(e) => setNewLayoutName(e.target.value)} placeholder="e.g. Day trading setup" />
            {isStaff && (
              <label className="flex items-start gap-3 rounded-lg border border-borderDefault bg-backgroundBody/40 px-3 py-3">
                <input
                  type="checkbox"
                  checked={saveAsGlobal}
                  onChange={(event) => setSaveAsGlobal(event.target.checked)}
                  className="mt-1 h-4 w-4 rounded border-borderDefault"
                />
                <div>
                  <div className="text-sm font-medium text-textPrimary">Make this layout global</div>
                  <div className="mt-1 text-xs text-textSecondary">Global studio layouts are available to all users and can only be edited by staff.</div>
                </div>
              </label>
            )}
          </div>
        </Modal>

        {/* Rename modal */}
        <Modal open={showRename} onClose={() => setShowRename(false)} title="Rename layout"
          footer={<>
            <Button variant="outline" onClick={() => setShowRename(false)} size="sm">Cancel</Button>
            <Button onClick={handleRenameLayout} size="sm" disabled={!newLayoutName.trim() || !canManageSelectedLayout}>Rename</Button>
          </>}
        >
          <div className="space-y-2">
            <label className="text-sm text-textSecondary">New name</label>
            <Input value={newLayoutName} onChange={(e) => setNewLayoutName(e.target.value)} placeholder={selectedLayout?.name || 'Layout name'} />
          </div>
        </Modal>

        {/* Delete confirm modal */}
        <Modal open={showDeleteConfirm} onClose={() => setShowDeleteConfirm(false)} title="Delete layout">
          <p className="text-sm">Are you sure you want to delete <span className="font-medium">{selectedLayout ? getLayoutDisplayName(selectedLayout) : ''}</span>? This action cannot be undone.</p>
          <div className="mt-4 flex justify-end gap-2">
            <Button variant="outline" size="sm" onClick={() => setShowDeleteConfirm(false)}>Cancel</Button>
            <Button variant="destructive" size="sm" onClick={handleDeleteLayout} disabled={!canManageSelectedLayout}>Delete</Button>
          </div>
        </Modal>

        {/* Widget picker modal */}
        <Modal open={showWidgetPicker} onClose={() => setShowWidgetPicker(false)} title="Add widget">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
            {Object.entries(widgetMeta)
              .filter(([type]) => !HIDDEN_WIDGET_TYPES.has(type))
              .map(([type, meta]) => (
              <button key={type} className="text-left rounded-lg border border-borderDefault hover:bg-secondary/10 p-3" onClick={() => { addWidgetOfType(type); setShowWidgetPicker(false); }}>
                <div className="font-medium">{meta.label}</div>
                <div className="mt-1 text-xs text-textSecondary">{meta.defaultW}x{meta.defaultH}</div>
              </button>
            ))}
          </div>
          <div className="mt-4 flex justify-end">
            <Button variant="outline" size="sm" onClick={() => setShowWidgetPicker(false)}>Close</Button>
          </div>
        </Modal>
      </div>
  );
}

export default function CustomStudioPage() {
  return (
    <StudioView />
  );
}
